import React, { useContext, useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Checkbox, Col, DatePicker, Form, InputNumber, Modal, Result, Row, Typography } from "antd";
import { Line } from "@ant-design/charts";
import moment from "moment";
import { FileExcelOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { AssetForecast } from "../data/AssetForecast";
import { AppContext } from "../state/State";

const { Paragraph, Title } = Typography;
const { RangePicker } = DatePicker;

interface ChartPoint {
    x: Date;
    y: number;
    category: string;
}

const PvEstimation: React.FC = () => {
    const context = useContext(AppContext);
    const [loading, setLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [chartData, setChartData] = useState<ChartPoint[]>([]);
    const [forecasts, setForecasts] = useState<AssetForecast[]>([]);
    const popupTimerRef = useRef<number | null>(null);
    const popupScheduledRef = useRef(false);
    const popupShownThisVisitRef = useRef(false);

    const clearPopupTimer = () => {
        if (popupTimerRef.current !== null) {
            window.clearTimeout(popupTimerRef.current);
            popupTimerRef.current = null;
        }
    };

    const scheduleCtaPopup = () => {
        if (popupScheduledRef.current || popupShownThisVisitRef.current || context.loggedIn) {
            return;
        }

        popupScheduledRef.current = true;
        popupTimerRef.current = window.setTimeout(() => {
            popupShownThisVisitRef.current = true;
            Modal.info({
                title: "Need continuous monitoring of curtailment losses?",
                okText: "Request a demo",
                cancelText: "Close",
                closable: true,
                width: 640,
                content: (
                    <div>
                        <Paragraph>This free tool provides single asset estimates.</Paragraph>
                        <Paragraph>Meteogen provides operational forecasting services for:</Paragraph>
                        <ul>
                            <li>Wind &amp; solar park owners</li>
                            <li>RES Aggregators</li>
                            <li>Asset managers</li>
                            <li>Investors</li>
                        </ul>
                        <Paragraph style={{ marginTop: 12, marginBottom: 8 }}>Get access to:</Paragraph>
                        <Paragraph style={{ marginBottom: 4 }}>✓ Best in class solar &amp; wind production forecasts</Paragraph>
                        <Paragraph style={{ marginBottom: 4 }}>✓ Historical curtailment analysis</Paragraph>
                        <Paragraph>✓ Daily expected production estimates</Paragraph>
                    </div>
                )
            });
        }, 3000);
    };

    useEffect(() => {
        document.title = "PV Curtailment Loss Estimator - Meteogen";
        return () => {
            clearPopupTimer();
        };
    }, []);

    const onFinish = async (values: any) => {
        clearPopupTimer();
        popupScheduledRef.current = false;
        setLoading(true);
        setHasError(false);
        setChartData([]);

        const from = (values["date-range"][0] as moment.Moment).utc();
        const to = (values["date-range"][1] as moment.Moment).utc();

        try {
            const response = await fetch("/api/Weather/PvEstimation", {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-type": "application/json"
                },
                body: JSON.stringify({
                    from: from.format("YYYY-MM-DDTHH:mm:ss"),
                    to: to.format("YYYY-MM-DDTHH:mm:ss"),
                    interval: "FifteenMinutes",
                    capacity: values.capacity,
                    capacityCap: values.capacityCap ?? null,
                    longitude: values.longitude,
                    latitude: values.latitude,
                    surfaceTilt: values.surfaceTilt,
                    eastTiltFrom: values.eastTiltFrom,
                    westTiltTo: values.westTiltTo,
                    azimuthFrom: values.azimuthFrom,
                    azimuthTo: values.azimuthTo,
                    bifacial: values.bifacial ?? false
                })
            });

            if (response.status !== 200) {
                setHasError(true);
                setLoading(false);
                return;
            }

            const data = (await response.json()) as AssetForecast[];
            data.forEach((x) => (x.validDate = moment(x.validDate).add(moment(x.validDate).toDate().getTimezoneOffset(), "minutes").toDate()));

            setForecasts(data);
            setChartData(
                data.map((x) => ({
                    x: x.validDate as Date,
                    y: x.power,
                    category: "Estimated Power"
                }))
            );
        } catch {
            setHasError(true);
        }

        setLoading(false);
    };

    const handleExport = () => {
        if (!forecasts || forecasts.length === 0) {
            return;
        }

        const header = ["Date (UTC)", "Estimated Power (kW)", "Irradiance (W/m²)"];
        const rows = forecasts.map((x) => [
            moment(x.validDate).format("YYYY-MM-DD HH:mm:ss"),
            Math.round(x.power * 1000) / 1000,
            Math.round(x.radiation * 1000) / 1000
        ]);

        const worksheet = XLSX.utils.aoa_to_sheet([header, ...rows]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "PV Estimation");
        XLSX.writeFile(workbook, `pv-estimation-${moment().utc().format("YYYYMMDD-HHmmss")}.xlsx`);
    };

    return (
        <Row>
            <Col xs={24} md={{ span: 18, offset: 3 }}>
                <Card>
                    <Title level={2}>PV Curtailment Loss Estimator</Title>
                    <Title level={3}>How much energy was your solar park prevented from producing?</Title>
                    <Paragraph>
                        When curtailments occur, the actual energy delivered to the grid no longer reflects the true solar resource available to your plant. Our
                        estimation engine reconstructs the production your PV park would have achieved under normal operating conditions.
                    </Paragraph>
                    <Paragraph>
                        Unlike generic PV calculators that rely on climatological averages, Meteogen uses{" "}
                        <strong>
                            high-resolution satellite observations, advanced numerical weather prediction data, machine learning models trained on operational
                            PV assets and site-specific plant characteristics.
                        </strong>{" "}
                        The result is a realistic estimate of the energy that was physically available but potentially lost due to curtailments.
                    </Paragraph>
                    <Paragraph>
                        Enter your park details and a date range, then instantly visualize estimated production and export the full series to Excel.
                    </Paragraph>

                    <Form
                        layout="vertical"
                        onFinish={onFinish}
                        initialValues={{
                            "date-range": [moment().utc().startOf("day").subtract(2, "days"), moment().utc().startOf("day")],
                            capacity: 1000,
                            elevation: 0,
                            surfaceTilt: 25,
                            eastTiltFrom: 0,
                            westTiltTo: 0,
                            azimuthFrom: 180,
                            azimuthTo: 180,
                            bifacial: false
                        }}
                    >
                        <Row gutter={16}>
                            <Col xs={24} md={3}>
                                <Form.Item label="Capacity (kW)" name="capacity" rules={[{ required: true, type: "number", min: 0.001 }]}>
                                    <InputNumber style={{ width: "100%" }} controls={false} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={3}>
                                <Form.Item label="Capacity cap (kW)" name="capacityCap" rules={[{ required: false, type: "number", min: 0 }]}>
                                    <InputNumber style={{ width: "100%" }} controls={false} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={3}>
                                <Form.Item label="Latitude" name="latitude" rules={[{ required: true, type: "number", min: -90, max: 90 }]}>
                                    <InputNumber style={{ width: "100%" }} controls={false} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={3}>
                                <Form.Item label="Longitude" name="longitude" rules={[{ required: true, type: "number", min: -180, max: 180 }]}>
                                    <InputNumber style={{ width: "100%" }} controls={false} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col xs={24} md={3}>
                                <Form.Item label="Surface tilt (&deg;)" name="surfaceTilt" rules={[{ required: true, type: "number", min: -90, max: 90 }]}>
                                    <InputNumber style={{ width: "100%" }} controls={false} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={3}>
                                <Form.Item label="East tilt from (&deg;)" name="eastTiltFrom" rules={[{ required: true, type: "number", min: -90, max: 90 }]}>
                                    <InputNumber style={{ width: "100%" }} controls={false} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={3}>
                                <Form.Item label="West tilt to (&deg;)" name="westTiltTo" rules={[{ required: true, type: "number", min: -90, max: 90 }]}>
                                    <InputNumber style={{ width: "100%" }} controls={false} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={3}>
                                <Form.Item label="Azimuth from (&deg;)" name="azimuthFrom" rules={[{ required: true, type: "number", min: -360, max: 360 }]}>
                                    <InputNumber style={{ width: "100%" }} controls={false} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={3}>
                                <Form.Item label="Azimuth to (&deg;)" name="azimuthTo" rules={[{ required: true, type: "number", min: -360, max: 360 }]}>
                                    <InputNumber style={{ width: "100%" }} controls={false} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={3} style={{ display: "flex", alignItems: "center" }}>
                                <Form.Item label=" " name="bifacial" valuePropName="checked">
                                    <Checkbox>Bifacial installation</Checkbox>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col xs={24} md={6}>
                                <Form.Item label="Date range" name="date-range" rules={[{ required: true }]}>
                                    <RangePicker style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row>
                            <Col>
                                <Button loading={loading} type="primary" htmlType="submit">
                                    Estimate production
                                </Button>
                            </Col>
                        </Row>
                    </Form>

                    {hasError && (
                        <Result
                            status="error"
                            title="Unable to estimate production"
                            subTitle="Please verify your input and try again."
                            extra={<Alert type="warning" message="If the requested period is outside data availability, estimation may not be possible." />}
                        />
                    )}

                    {chartData.length > 0 && (
                        <>
                            <Row style={{ marginTop: "16px", marginBottom: "12px" }}>
                                <Col flex="auto">
                                    <Title level={4} style={{ margin: 0 }}>
                                        Estimated production profile
                                    </Title>
                                </Col>
                                <Col>
                                    <Button
                                        type="default"
                                        style={{ backgroundColor: "green", color: "white", borderColor: "green" }}
                                        onClick={handleExport}
                                        icon={<FileExcelOutlined />}
                                    >
                                        Export to Excel
                                    </Button>
                                </Col>
                            </Row>

                            <Line
                                data={chartData}
                                height={420}
                                xField="x"
                                yField="y"
                                seriesField="category"
                                yAxis={{ title: { text: "Power (kW)" } }}
                                xAxis={{ type: "time" }}
                                tooltip={{
                                    formatter: (datum: any) => ({
                                        title: moment(datum.x).format("LLL"),
                                        name: datum.category,
                                        value: Math.round(datum.y * 1000) / 1000
                                    })
                                }}
                                onReady={(plot: any) => {
                                    plot.once("afterrender", () => {
                                        scheduleCtaPopup();
                                    });
                                }}
                            />
                        </>
                    )}
                </Card>
            </Col>
        </Row>
    );
};

export default PvEstimation;
